import type { ProgressionPhaseCode } from './phase-goals';

export interface GlossaryEntry {
  term: string;
  definition: string;
}

export const PROGRESSION_GLOSSARY: GlossaryEntry[] = [
  {
    term: 'Stack',
    definition: 'Neutral alignment of the diaphragm over the pelvic floor — the foundation for all movement options.',
  },
  {
    term: 'Occupancy',
    definition: 'The ability to achieve stable stance on a side and shift out of it — a core gait requirement.',
  },
  {
    term: 'Lateralization',
    definition: 'Asymmetric weighting toward one side. Training restores the non-lateralized side first, then the lateralized side.',
  },
  {
    term: 'APT',
    definition: 'Anterior Pelvic Tilt — global anterior orientation. Phase 1 restores posterior expansion.',
  },
  {
    term: 'Swayback',
    definition: 'Posterior compression pattern. Phase 1 restores anterior expansion.',
  },
  {
    term: 'Grounding',
    definition: 'Neurological capacity to produce and sense force through the foot during stance.',
  },
];

export interface TransitionBridge {
  name: string;
  description: string;
  steps: string[];
}

export interface PhaseNarrative {
  code: ProgressionPhaseCode;
  index: number;
  title: string;
  subtitle: string;
  summary: string;
  focus: string;
  patternNote?: { apt: string; sb: string };
  stayHereIf: string;
  transition?: TransitionBridge;
  sampleDrills: string[];
}

export const PHASE_NARRATIVES: PhaseNarrative[] = [
  {
    code: 'P1',
    index: 1,
    title: 'Restore expansion',
    subtitle: 'Bilateral mobility & the stack',
    summary:
      'Erase widespread compensations and rebuild foundational range of motion on both sides. The goal is an easy full breath, a neutral stack, and basic alignment before any asymmetrical work begins.',
    focus: 'Bilateral positional drills that rebuild range of motion and breathing mechanics.',
    patternNote: {
      apt: 'Restore posterior expansion — ribs back, pelvis toward a neutral stack.',
      sb: 'Restore anterior expansion — open the front rib cage and pelvis.',
    },
    stayHereIf:
      'Gains do not hold after the Walking Test, or old patterns return as soon as the client moves freely.',
    transition: {
      name: 'Walking Test',
      description:
        'After initial improvements, the client walks 20–50 yards and you re-test. Gains should hold at roughly 80–90%.',
      steps: [
        'Re-test all Phase 1 goals after the interventions.',
        'Walk 20–50 yards at a natural pace.',
        'Re-test immediately — if 80–90% of gains hold, advance.',
      ],
    },
    sampleDrills: ['Sidelying Breathing (90/90)', 'Sidelying Grounding', '90/90 Hip Lift'],
  },
  {
    code: 'P2',
    index: 2,
    title: 'Train the non-lateralized side',
    subtitle: 'Build occupancy & control asymmetry',
    summary:
      'Conquer asymmetrical limitations. Teach the body to load and unload each side independently, building occupancy on the side that historically avoided stance.',
    focus: 'Unilateral, asymmetrical drills emphasizing side-specific internal/external rotation and grounding.',
    stayHereIf:
      'Side-specific assessments still lack control, or symmetry has not improved after the Occupancy Test.',
    transition: {
      name: 'Occupancy Test',
      description:
        'Perform Introductory Abduct & Shift on the originally lateralized side. Measurements should stay the same or improve bilaterally.',
      steps: [
        'Identify the originally lateralized side from the SoP assessment.',
        'Perform Introductory Abduct & Shift on that side.',
        'Re-test — if measurements hold or improve bilaterally, advance.',
      ],
    },
    sampleDrills: ['Sidelying Adductor Pullback', 'Sidelying Glute Max', 'Introductory Abduct & Shift'],
  },
  {
    code: 'P3',
    index: 3,
    title: 'Integrate on the lateralized side',
    subtitle: 'Load, power & athletic output',
    summary:
      'Integrate restored mechanics into loaded, dynamic, and sport-specific movement. Build genuine durability on the originally lateralized side while holding onto every Phase 2 gain.',
    focus: 'High-level strength, power, and weight-room integration under load.',
    stayHereIf:
      'Compensations return under load, grounding scores drop, or performance metrics regress.',
    sampleDrills: ['Standing Abduct & Shift', 'PRI Hip Shift Squat', 'Reciprocal Step-Through'],
  },
];

export interface TroubleshootingStep {
  id: string;
  order: number;
  title: string;
  prompt: string;
  checks: string[];
  resolution: string;
}

export const TROUBLESHOOTING_INTRO =
  'If a client plateaus, do not just add volume. Adaptations should be similar even 4+ hours after a drill — if they are not, work through these checks in order before advancing.';

export const TROUBLESHOOTING_STEPS: TroubleshootingStep[] = [
  {
    id: 'execution',
    order: 1,
    title: 'Check execution',
    prompt: 'Is the work actually being done well?',
    checks: [
      'Watch the client perform every exercise.',
      'Confirm optimal form and the intended muscle engagement.',
    ],
    resolution: 'If form is off, run another week with corrections before moving on.',
  },
  {
    id: 'lifestyle',
    order: 2,
    title: 'Investigate lifestyle',
    prompt: 'Is recovery being undermined outside the gym?',
    checks: [
      'Excessive stress or poor life balance.',
      'Mouth-open sleep or snoring; waking up worse.',
      'Under 5,000 steps per day.',
      'Sitting 2+ hours without movement breaks.',
    ],
    resolution: 'Apply targeted fixes — meditation, mouth tape, more steps, an hourly drill break.',
  },
  {
    id: 'cranial',
    order: 3,
    title: 'Cranial & cervical influences',
    prompt: 'Is something upstream blocking adaptation?',
    checks: ['Tongue position and tongue tie.', 'Bite test.'],
    resolution: 'Refer out to a specialist when indicated, then re-test once resolved.',
  },
];

export const CORE_IDEA =
  "Restore and optimize the body's natural ability to shift and rotate — dynamically balancing internal compression and external expansion.";

export const UNDERLYING_PRINCIPLE =
  'Always assess, intervene, and re-assess. The system is interconnected: fix the fundamental limitations and the symptoms often resolve on their own. No fluff, just mechanics.';

export const PROGRESSION_DIAGRAM_PDF = '/Week_7_Progression_Model_2_.pdf';

export const JOURNEY_ENDPOINTS = {
  start: { label: 'Dysfunction', detail: 'Compensations, lost range, asymmetry' },
  end: { label: 'Optimal output', detail: 'Durable, symmetrical, athletic movement' },
};
